import { useState } from "react";
import { useToasts } from "react-toast-notifications";
import { useGetEmailsTeamID } from "hooks/useGetEmailsTeamID";

export type EmailWithTeamMap = Map<string, IEmailWithTeam>;

export interface IEmailWithTeam {
  email: string;
  teamId: number | null;
  status: "pending" | "success" | "error";
}

export function useValidateEmails() {
  const [emailsMap, setEmailsMap] = useState<EmailWithTeamMap>(new Map());
  const { getEmailsTeamID, ...mutationRest } = useGetEmailsTeamID();
  const { addToast } = useToasts();

  const validateEmails = async (emails: IEmailWithTeam[]) => {
    const emailsToValidate = emails.filter(({ email, status }) =>
      emailsMap.has(email)
        ? emailsMap.get(email)?.status === "error"
        : status === "pending",
    );

    if (!emailsToValidate.length) {
      return;
    }

    try {
      const emailsTeamsId = await getEmailsTeamID(
        emailsToValidate.map(({ email }) => email),
      );

      if (!emailsTeamsId) {
        throw new Error("No emails found");
      }

      setEmailsMap(prevMap => {
        const newMap = new Map(prevMap);
        emailsTeamsId.forEach(({ email, team_id }) => {
          if (newMap.has(email)) {
            newMap.set(email, {
              email,
              teamId: team_id,
              status: "success",
            });
          }
        });
        return newMap;
      });
    } catch (error) {
      addToast("Failed to validate emails. Please try again!", {
        appearance: "error",
      });

      setEmailsMap(prevMap => {
        const newMap = new Map(prevMap);
        emailsToValidate.forEach(({ email }) => {
          if (newMap.get(email)?.status === "pending") {
            newMap.set(email, {
              email,
              teamId: null,
              status: "error",
            });
          }
        });
        return newMap;
      });
    }
  };

  return {
    ...mutationRest,
    emailsMap,
    setEmailsMap,
    validateEmails,
  };
}
